/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-05-20 00:00:00
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React from 'react';
import validator from 'validator';
import {
  Card,
  TextStyle,
  FormLayout,
  Icon,
  SkeletonBodyText,
  Popover,
  Thumbnail,
  Banner
} from '@shopify/polaris';
import {
  ChevronLeftMinor,
  ChevronRightMinor,
  PinMinor,
  QuestionMarkMajor,
  ViewMinor
} from '@shopify/polaris-icons';
import { withI18n } from '@shopify/react-i18n';
import { connect } from 'react-redux';
import history from 'src/routes/history';
import { getFormValues } from 'redux-form';
import emptyImage from 'src/assets/images/filter-tree/empty-option.png';
import noProductImage from 'src/assets/images/product/no-product-image.png';
import { getShop } from 'src/helpers';
import { waitFor } from 'src/helpers/time-helper';

import { PREVIEW_PRODUCT_PER_PAGE, PREVIEW_PRODUCT_PER_ROW } from 'src/constants/merchandising-v2';
import { formatMoney } from 'src/helpers/money-format';
import { routes } from 'src/routes';

import PlaceHolderBannerImg from 'src/assets/images/merchandising/banner-placeholder.png';

function usePrevious(value) {
  const ref = React.useRef();
  React.useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}
const PreviewMerchandising = props => {
  const {
    i18n,
    products = [],
    loading,
    renderingPreviewSuccess,
    duplicating,
    activeStrategy,
    setPreviewQuery,
    previewQuery
  } = props;
  const [showLoading, setShowLoading] = React.useState(true);
  const [activeTooltip, setActiveTooltip] = React.useState(false);
  const {
    collection,
    queries,
    strategies,
    products: strategyProducts,
    isEnablePerformanceRanking
  } = props.formValues || {};

  const filterBannerStrategies = strategies.filter(
    strategy =>
      strategy.type === 'banner' &&
      strategy.name &&
      strategy.link &&
      validator.isURL(strategy.link) &&
      strategy.imgSrc &&
      !_.isEmpty(strategy?.imgSrc)
  );
  const previousQueries = usePrevious(queries);

  React.useEffect(() => {
    getProducts(1);
    // eslint-disable-next-line
  }, [collection, strategies, strategyProducts, isEnablePerformanceRanking]);

  let previewInterval = null;
  React.useEffect(() => {
    let query = previewQuery;
    if (previousQueries && previousQueries.length === queries.length) {
      // change term
      for (let i = 0; i < queries.length; i++) {
        if (previousQueries[i] !== queries[i]) {
          query = queries[i];
          break;
        }
      }
      if (query === previewQuery) {
        return;
      }
    } else if (previousQueries && previousQueries.length === queries.length + 1) {
      // delete term
      const deletedTerms = previousQueries.filter(t => !queries.includes(t));
      if (deletedTerms.includes(previewQuery)) {
        setPreviewQuery('');
        query = '';
      }
    }
    // eslint-disable-next-line
    previewInterval = setTimeout(() => {
      setPreviewQuery(query);
      getProducts(1, true, query);
    }, 500);
    return () => clearTimeout(previewInterval);
  }, [queries]);

  const getProducts = async (page, forceRender, query) => {
    await waitFor(300);

    if (loading || duplicating || !renderingPreviewSuccess) {
      return;
    }
    setShowLoading(false);
    clearTimeout(previewInterval);
    const { setPreviewPage } = props;
    await setPreviewPage(page, forceRender, query);
  };

  const renderProductGridTitle = () => {
    const { i18n, products = [], ruleType, themeSetupV2 } = props;

    const getContent = () => {
      if (ruleType === 'search') {
        return i18n.translate('AdminHTML.MerchandisingV2.Preview.sort-tooltip-search', {
          pin: (
            <TextStyle variation="strong">
              {i18n.translate('AdminHTML.MerchandisingV2.Rule.pinButtonTitle')}
            </TextStyle>
          ),
          hide: (
            <TextStyle variation="strong">
              {i18n.translate('AdminHTML.MerchandisingV2.Rule.hideButtonTitle')}
            </TextStyle>
          ),
          filter: (
            <TextStyle variation="strong">
              {i18n.translate('AdminHTML.MerchandisingV2.Rule.filterButtonTitle')}
            </TextStyle>
          ),
          boost: (
            <TextStyle variation="strong">
              {i18n.translate('AdminHTML.MerchandisingV2.Rule.boostButtonTitle')}
            </TextStyle>
          ),
          demote: (
            <TextStyle variation="strong">
              {i18n.translate('AdminHTML.MerchandisingV2.Rule.demoteButtonTitle')}
            </TextStyle>
          ),
          sortByRelevance: (
            <TextStyle variation="strong">
              {i18n.translate('AdminHTML.MerchandisingV2.Preview.sortByRelevance')}
            </TextStyle>
          ),
          link: (
            <TextStyle variation="strong">
              <span
                className="link-tooltip"
                onClick={() => {
                  if (themeSetupV2) {
                    history.push({
                      pathname: routes.customSorting.view(),
                      state: { selectedTab: 0 }
                    });
                    return;
                  }
                  history.push({
                    pathname: routes.filterSettings.view(),
                    state: { selectedTab: 1 }
                  });
                }}>
                {i18n.translate(
                  `AdminHTML.MerchandisingV2.Preview.${
                    themeSetupV2 ? 'tooltip-link' : 'tooltip-linkV1'
                  }`
                )}
              </span>
            </TextStyle>
          )
        });
      }

      return i18n.translate('AdminHTML.MerchandisingV2.SortByManual.sortByManualTooltip', {
        pin: (
          <TextStyle variation="strong">
            {i18n.translate('AdminHTML.MerchandisingV2.Rule.pinButtonTitle')}
          </TextStyle>
        ),
        hide: (
          <TextStyle variation="strong">
            {i18n.translate('AdminHTML.MerchandisingV2.Rule.hideButtonTitle')}
          </TextStyle>
        ),
        boost: (
          <TextStyle variation="strong">
            {i18n.translate('AdminHTML.MerchandisingV2.Rule.boostButtonTitle')}
          </TextStyle>
        ),
        demote: (
          <TextStyle variation="strong">
            {i18n.translate('AdminHTML.MerchandisingV2.Rule.demoteButtonTitle')}
          </TextStyle>
        ),
        relevance: (
          <TextStyle variation="strong">
            {i18n.translate('AdminHTML.MerchandisingV2.SortByManual.relevance')}
          </TextStyle>
        ),
        manual: (
          <TextStyle variation="strong">
            {i18n.translate('AdminHTML.MerchandisingV2.SortByManual.manual')}
          </TextStyle>
        ),
        path: (
          <TextStyle variation="strong">
            <span
              className="link-tooltip"
              onClick={() => {
                if (themeSetupV2) {
                  history.push({
                    pathname: routes.customSorting.view(),
                    state: { selectedTab: 0 }
                  });
                  return;
                }
                history.push({ pathname: routes.filterSettings.view(), state: { selectedTab: 1 } });
              }}>
              {i18n.translate(
                `AdminHTML.MerchandisingV2.SortByManual.${
                  themeSetupV2 ? 'manualPath' : 'manualPath01'
                }`
              )}
            </span>
          </TextStyle>
        )
      });
    };

    const activator = (
      <div onMouseMove={() => setActiveTooltip(true)} onMouseLeave={() => setActiveTooltip(false)}>
        <Icon source={QuestionMarkMajor} color="base" />
      </div>
    );
    return (
      <div className="productGrid">
        <div className="set-rule-strategies-title">
          {ruleType === 'search'
            ? i18n.translate('AdminHTML.MerchandisingV2.Preview.sort-by-relevance')
            : i18n.translate('AdminHTML.MerchandisingV2.SortByManual.sortByManual')}

          <Popover
            active={activeTooltip}
            activator={activator}
            autofocusTarget="first-node"
            onClose={() => setActiveTooltip(false)}>
            <div
              onMouseLeave={() => setActiveTooltip(false)}
              onMouseMove={() => setActiveTooltip(true)}
              style={{ padding: '10px', maxWidth: '300px' }}>
              {getContent()}
            </div>
          </Popover>
        </div>

        {/* Pagination */}
        {products.length ? renderPaginationBar() : ''}
      </div>
    );
  };

  const renderNoResult = () => {
    const { i18n } = props;

    return (
      <div className="noResult">
        <img
          src={emptyImage}
          alt={i18n.translate('AdminHTML.MerchandisingV2.Preview.no_result_found')}
        />
        <p className="content">
          {i18n.translate('AdminHTML.MerchandisingV2.Preview.no_result_found')}
        </p>
      </div>
    );
  };

  const renderProductList = () => {
    const { products = [], moneyFormat, formValues = {}, i18n, previewPage } = props;
    const { strategies = [] } = formValues;

    if (!products.length) return '';

    const cloneProducts = _.cloneDeep(products);

    if (filterBannerStrategies.length > 0 && previewPage === 1) {
      filterBannerStrategies.forEach(banner => {
        if (banner.position >= cloneProducts.length) {
          cloneProducts.push(banner);
        } else cloneProducts.splice(banner.position, 0, banner);
      });
    }

    if (filterBannerStrategies.length === 0 && activeStrategy === 'banner') {
      const currentBanner = strategies.find(strategy => strategy.type === 'banner');
      const currentPosition = _.get(currentBanner, 'position', 0);
      cloneProducts.splice(currentPosition, 0, {
        type: 'placeholderBanner'
      });
    }

    // Get pin products
    const pinValue = strategies ? _.find(strategies, { type: 'pin' }) : [];
    const positions = _.get(pinValue, 'pinPositions', []);
    const pinProducts = positions.map(position => {
      return _.get(position, 'id.id', null);
    });

    // Render 4 products per row, 3 lines of products
    const productsPerRow = [];
    for (let i = 0; i < cloneProducts.length; i++) {
      const rowIndex = Math.floor(i / PREVIEW_PRODUCT_PER_ROW);
      if (!productsPerRow[rowIndex]) productsPerRow[rowIndex] = [];
      productsPerRow[rowIndex].push(products[i]);
    }
    const shop = getShop();

    // cause we use space-between, so fill out the row with empty block
    const leftToFillRow = 4 - (cloneProducts.length % 4);

    const renderProductList = () => {
      return (
        <div className="productRow">
          {cloneProducts.map((product, productIndex) => {
            if (_.has(product, 'type') && product.type === 'banner') {
              const imgSrc = _.get(product, 'imgSrc', {});
              const openInNewTab = _.get(product, 'openInNewTab', true);
              const link = _.get(product, 'link', null);
              let file = imgSrc?.url;
              if (file) {
                file =
                  typeof imgSrc?.url === 'string'
                    ? imgSrc.url
                    : window.URL.createObjectURL(imgSrc.url);
              } else file = '';
              return (
                <div
                  className="productItem bannerItem"
                  key={'banner-' + productIndex}
                  onClick={() => window.open(link, openInNewTab ? '_blank' : '_self')}>
                  <Thumbnail size="small" alt={imgSrc.name} source={file} />
                </div>
              );
            }

            if (product.type === 'placeholderBanner') {
              return (
                <div className="productItem bannerItem" key={'bannerPlaceholder-' + productIndex}>
                  <Thumbnail
                    size="small"
                    alt={'placeholder-banner'}
                    source={PlaceHolderBannerImg}
                  />
                </div>
              );
            }

            const { id, product_id, title = '', price_min = 0, images = {}, value } = product;
            const imageUrl = _.isEmpty(images) ? noProductImage : images[Object.keys(images)[0]];
            const url = `https://${shop}/admin/products/${value || product_id || id}`;
            return (
              <div className="productItem" key={id + '-' + productIndex}>
                {/* Pin icon */}
                {_.includes(pinProducts, id) ? (
                  <span className="pin">
                    <Icon source={PinMinor} color="base" />
                  </span>
                ) : (
                  ''
                )}
                <span style={{ display: 'none' }}>{id}</span>
                <div
                  className={`product-image ${_.isEmpty(images) ? 'product-image--no-image' : ''}`}>
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    <span>
                      <Icon source={ViewMinor} />
                      <span className="view-product">
                        {i18n.translate('AdminHTML.MerchandisingV2.Rule.viewProduct')}
                      </span>
                    </span>
                  </a>
                  {_.isEmpty(images) ? (
                    <img className="noImage" src={imageUrl} alt={title} />
                  ) : (
                    <img className="image" src={imageUrl} alt={title} />
                  )}
                </div>
                <p className="title">{title}</p>
                <p>
                  <span className="priceMin">{formatMoney(price_min, moneyFormat)}</span>
                </p>
              </div>
            );
          })}
          {leftToFillRow < 4 &&
            _.range(leftToFillRow).map(index => (
              <div className="productItem" key={index + '-blank'}></div>
            ))}
        </div>
      );
    };

    return <div className="productList">{renderProductList()}</div>;
  };

  const renderPaginationBar = () => {
    let { total = 0, previewPage: page } = props;

    // ensure data page is number
    if (typeof page !== 'number') {
      page = 1;
    }
    // Calculate total page
    const totalPage = Math.ceil(total / PREVIEW_PRODUCT_PER_PAGE);

    // Initialize rule items
    let rules = [];
    if (totalPage <= 4) {
      for (let i = 1; i <= totalPage; i++) {
        rules.push(i);
      }
    } else {
      // >= 5
      // Always have 3 first pages
      rules = [1, 2, 3];

      // Add ... in case of first and second page
      if (page === 1 || page === 2) {
        rules.push('...');
      }

      // Add page before current page
      const prevPage = page - 1;
      if (prevPage > 0 && !_.includes(rules, prevPage)) {
        if (prevPage > 4) {
          rules.push('...');
        }
        rules.push(prevPage);
      }

      // Add current page
      if (!_.includes(rules, page)) {
        rules.push(page);
      }

      // Add page after current page
      const nextPage = page + 1;
      if (nextPage <= totalPage && !_.includes(rules, nextPage)) {
        rules.push(nextPage);
        if (nextPage < totalPage - 1) {
          rules.push('...');
        }
      }

      // Latest page
      if (page !== totalPage && !_.includes(rules, totalPage)) {
        rules.push(totalPage);
      }
    }

    return (
      <div className="pagination">
        {/* Previous page */}
        {page > 1 ? (
          <span className="prev icon link" onClick={() => getProducts(page - 1, true)}>
            <Icon source={ChevronLeftMinor} color="subdued" />
          </span>
        ) : (
          <span className="prev icon disabled">
            <Icon source={ChevronLeftMinor} color="subdued" />
          </span>
        )}
        {rules.map((item, index) => {
          // Current page
          if (item !== '...' && item === page) {
            return (
              <span key={'item-' + index} className="item selected">
                {item}
              </span>
            );
          }

          // Next page
          // If item is ..., get page of previous page + 1
          const pageIndex = item !== '...' ? item : rules[index - 1] + 1;

          // Another pages
          return (
            <span
              key={'item-' + index}
              className={item === '...' ? 'item link point' : 'item link'}
              onClick={() => getProducts(pageIndex, true)}>
              {item}
            </span>
          );
        })}

        {/* Next page */}
        {page < totalPage ? (
          <span className="next icon link" onClick={() => getProducts(page + 1, true)}>
            <Icon source={ChevronRightMinor} color="subdued" />
          </span>
        ) : (
          <span className="next icon disabled">
            <Icon source={ChevronRightMinor} color="subdued" />
          </span>
        )}
      </div>
    );
  };

  const notifyStrategyStatus = (type = 'success', content) => {
    return (
      <div className="banner-status">
        <Banner status={type}>{content}</Banner>
      </div>
    );
  };

  // const renderQueries = () => {
  //   const { formValues = {}, previewQuery } = props;
  //   const { queries = [] } = formValues;

  //   const list = queries.map(q => _.trim(q)).filter(q => q);
  //   if (list.length === 0) {
  //     return <></>;
  //   }
  //   return (
  //     <div style={{ marginBottom: '0.75rem' }}>
  //       <TextStyle>{i18n.translate(`AdminHTML.MerchandisingV2.Preview.search_query`)}</TextStyle>
  //       <div>
  //         {list.map((query, index) => {
  //           let isActive;
  //           if (previewQuery) {
  //             isActive = query === previewQuery.trim();
  //           } else {
  //             isActive = index === list.length - 1;
  //           }
  //           return (
  //             <button
  //               style={{
  //                 border: 0,
  //                 backgroundColor: isActive ? '#C9CCD0' : '#E4E5E7',
  //                 borderRadius: 4,
  //                 padding: '4px 8px',
  //                 marginRight: 10,
  //                 marginTop: 5,
  //                 cursor: 'pointer'
  //               }}
  //               onClick={() => getProducts(1, true, query)}>
  //               <TextStyle>{query}</TextStyle>
  //             </button>
  //           );
  //         })}
  //       </div>
  //     </div>
  //   );
  // };

  return (
    <Card
      title={
        <div className="">
          <p className="preview-title">
            {i18n.translate('AdminHTML.MerchandisingV2.Common.preview')}
          </p>
          <p className="preview-content">
            {i18n.translate('AdminHTML.MerchandisingV2.Preview.content')}
          </p>
        </div>
      }
      sectioned>
      <div className="preview">
        {(!renderingPreviewSuccess || duplicating || loading) && showLoading ? (
          <div className="product-loading">
            <FormLayout>
              <SkeletonBodyText lines={3} />
              <SkeletonBodyText lines={3} />
              <SkeletonBodyText lines={3} />
            </FormLayout>
          </div>
        ) : (
          <>
            <div>
              {/* Notify apply strategies */}
              {filterBannerStrategies.length > 0 &&
                notifyStrategyStatus(
                  'success',
                  i18n.translate('AdminHTML.MerchandisingV2.Preview.alertBanner')
                )}

              {/* Filter conditions */}
              {/* {renderQueries()} */}

              {/* Product grid */}
              {renderProductGridTitle()}

              {/* No result */}
              {/* {success && !products.length ? renderNoResult() : ''} */}
              {!products.length ? renderNoResult() : ''}
            </div>

            {/* Product list */}
            {products.length ? renderProductList() : ''}
          </>
        )}
      </div>
    </Card>
  );
};

const mapDispatchToProps = (dispatch, ownProps) => ({});

const mapStateToProps = (state, props) => {
  const formValues = getFormValues('MerchandisingV2RuleForm')(state);
  const optionsList = _.get(state.filterData.productAttributes, 'optionsList', []);
  // Get format money, help to display price
  const moneyFormat = _.get(state.oauth.tenantApp, 'externalInfo.money_format', '');

  return {
    formValues,
    optionsList,
    moneyFormat,
    themeSetupV2: _.get(state.oauth, 'tenantApp.adminAppFeatures.themeSetupV2', false),
    renderingPreviewSuccess: _.get(state.merchandisingV2, 'renderingPreviewSuccess', true),
    loading: _.get(state.merchandisingV2, 'loading', true),
    duplicating: _.get(state.merchandisingV2, 'duplicating', true),
    renderingPreview: _.get(state.merchandisingV2, 'renderingPreview', true)
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(PreviewMerchandising));
